import type { Event, EventHint } from "@sentry/react";

export const CHUNK_RELOAD_KEY = "chunk_reloaded_at";

// A stale-chunk reload is allowed at most once per window. The guard is
// time-based (not cleared on boot) because a route chunk can fail *after* the
// app shell mounts — clearing on mount let a stale index.html loop forever.
export const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  "error loading dynamically imported module",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "reading 'AppRoutes'",
] as const;

export function isChunkLoadError(message: string): boolean {
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export function hasRecentChunkReload(): boolean {
  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
  return (
    Number.isFinite(lastReloadAt) &&
    Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS
  );
}

export function reloadWithCacheBust(): void {
  sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", Date.now().toString());
  window.location.replace(url.toString());
}

// Automatic recovery path: skipped if we already reloaded within the cooldown.
export function reloadForStaleChunks(): boolean {
  if (hasRecentChunkReload()) {
    return false;
  }

  reloadWithCacheBust();
  return true;
}

export function shouldDropSentryEvent(event: Event, hint?: EventHint): boolean {
  const messages: string[] = [];

  if (event.message) {
    messages.push(event.message);
  }

  for (const exception of event.exception?.values ?? []) {
    if (exception.value) {
      messages.push(exception.value);
    }
    if (exception.type) {
      messages.push(exception.type);
    }
  }

  const original = hint?.originalException;
  if (original instanceof Error) {
    messages.push(original.message);
  } else if (typeof original === "string") {
    messages.push(original);
  }

  return messages.some((message) => isChunkLoadError(message));
}
